import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/lib/api';
import { 
  Bell, 
  FileText, 
  TestTube, 
  Ship, 
  MessageSquare, 
  ShoppingCart,
  CheckCircle2,
  Clock,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const notificationIcons = {
  quote: <FileText className="w-5 h-5" />,
  quote_response: <FileText className="w-5 h-5" />,
  sample: <TestTube className="w-5 h-5" />,
  sample_update: <TestTube className="w-5 h-5" />,
  import: <Ship className="w-5 h-5" />,
  import_update: <Ship className="w-5 h-5" />,
  message: <MessageSquare className="w-5 h-5" />,
  order: <ShoppingCart className="w-5 h-5" />,
  order_update: <ShoppingCart className="w-5 h-5" />,
};

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isUSA } = useLocale();
  const { resetNotificationCount } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      resetNotificationCount(0); // Update real-time counter
      toast.success(isUSA ? 'All notifications marked as read' : 'Todas las notificaciones marcadas como leídas');
    } catch (error) {
      toast.error(isUSA ? 'Error marking notifications' : 'Error al marcar notificaciones');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="notifications-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="page-title">{isUSA ? 'Notifications' : 'Notificaciones'}</h1>
          <p className="page-subtitle">{unreadCount} {isUSA ? 'unread' : 'sin leer'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => navigate('/dashboard/notifications/preferences')}
            title={isUSA ? 'Notification settings' : 'Configurar notificaciones'}
          >
            <Settings className="w-4 h-4" />
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead}>
              <CheckCircle2 className="mr-2 w-4 h-4" />
              {isUSA ? 'Mark all as read' : 'Marcar todas como leídas'}
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="empty-state">
          <Bell className="empty-state-icon" />
          <h3 className="empty-state-title">{isUSA ? 'You have no notifications' : 'No tienes notificaciones'}</h3>
          <p className="empty-state-text">
            {isUSA ? 'Activity notifications will appear here' : 'Las notificaciones de tu actividad aparecerán aquí'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <Card 
              key={notification.notification_id}
              className={`card-industrial cursor-pointer transition-colors ${
                !notification.read ? 'border-l-4 border-l-emerald-500 bg-emerald-50/50' : ''
              }`}
              onClick={() => handleMarkRead(notification.notification_id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    !notification.read ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {notificationIcons[notification.type] || <Bell className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {notification.title}
                        </h4>
                        <p className="text-sm text-slate-600 mt-0.5">
                          {notification.message}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500 flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        {new Date(notification.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                      </div>
                    </div>
                    {notification.link && (
                      <Link 
                        to={notification.link}
                        className="text-sm text-emerald-600 hover:text-emerald-700 mt-2 inline-block"
                      >
                        {isUSA ? 'View details →' : 'Ver detalles →'}
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
